import type { FC } from '../../../../lib/teact/teact';
import { memo, useState } from '../../../../lib/teact/teact';
import { getActions, withGlobal } from '../../../../global';

import type { GlobalState } from '../../../../global/types';

import { navigate } from '../../../app/store/bileliContext.store';
import { t } from '../../../shared/i18n';
import { useProfile } from '../viewmodel/useProfile';

import Icon from '../../../shared/ui/Icon';

import './ProfilePanel.scss';

type SessionUser = NonNullable<GlobalState['bileliAuth']['user']>;

type StateProps = {
  user?: SessionUser;
};

// Bileli · view — the PROFILE page (design Screen 9), bi-modal (Учёба + Препод). Identity card (name is
// the only Bileli-editable field — avatar/username live in the Telegram half and hand off there), the
// student stats card (level derived from /me/stats XP), interface settings (KY⇄RU + theme), and the
// Telegram hand-off rows. Deliberately NO sign-out: that is a full Telegram logout (Telegram settings).
// PURELY PERSONAL since 2026-07-15: the org (school) cards moved to the «Команда» teach tab (TeamView).
const ProfilePanel: FC<StateProps> = ({ user }) => {
  const vm = useProfile(Boolean(user?.avatarUrl));
  const { setSharedSettingOption } = getActions();

  if (!user) {
    return (
      <div className="BProfile">
        <div className="BProfile__inner">
          <p className="b-text-muted">{t('Профиль загружается…')}</p>
        </div>
      </div>
    );
  }

  const initial = (user.displayName ?? 'B').trim()[0]?.toUpperCase() ?? 'B';
  const goMode = (mode: 'learn' | 'teach') => {
    setSharedSettingOption({ activeMode: mode });
    navigate(mode === 'teach' ? 'teacher-courses' : 'dashboard');
  };

  return (
    <div className="BProfile custom-scroll">
      <div className="BProfile__inner">
        <h1 className="b-h2">{t('Профиль')}</h1>

        {/* Identity */}
        <section className="b-card BProfile__card BProfile__identity">
          <div className={`BProfile__avatar-wrap ${vm.isSyncingAvatar ? 'BProfile__avatar-wrap--busy' : ''}`}>
            {user.avatarUrl ? (
              <img className="BProfile__avatar" src={user.avatarUrl} alt="" />
            ) : (
              <div className="b-avatar BProfile__avatar BProfile__avatar--initial" aria-hidden>{initial}</div>
            )}
          </div>
          <div className="BProfile__identity-main">
            <NameField
              key={user.id}
              initial={user.displayName ?? ''}
              isSaving={vm.isSavingName}
              error={vm.nameError}
              onSave={vm.saveName}
            />
            <div className="BProfile__meta">
              <span>{user.phone}</span>
              {user.telegramUsername && <span>{`@${user.telegramUsername}`}</span>}
            </div>
            <div className="BProfile__roles">
              {/* Bileli: icon system replaces the mode emoji — .b-pill is inline-flex, its gap spaces icon/label */}
              <button type="button" className="b-pill" onClick={() => goMode('learn')}>
                <Icon name="graduation-cap" />
                {t('Учёба')}
              </button>
              <button type="button" className="b-pill" onClick={() => goMode('teach')}>
                <Icon name="pencil" />
                {t('Преподавание')}
              </button>
            </div>
            {/* Telegram-first photo (user decision): sync from Telegram when it has one, hand off when not. */}
            <div className="BProfile__avatar-actions">
              {vm.telegramHasPhoto ? (
                <button
                  type="button"
                  className="b-btn b-btn-secondary b-btn-sm"
                  disabled={vm.isSyncingAvatar}
                  onClick={vm.syncAvatar}
                >
                  {vm.isSyncingAvatar ? t('Обновляем…') : (
                    <>
                      <Icon name="camera" />
                      {t('Обновить фото из Telegram')}
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  className="b-btn b-btn-secondary b-btn-sm"
                  onClick={vm.openTelegramProfile}
                >
                  <Icon name="camera" />
                  {t('Добавить фото в Telegram')}
                </button>
              )}
              {user.avatarUrl && (
                <button
                  type="button"
                  className="BProfile__avatar-remove"
                  disabled={vm.isSyncingAvatar}
                  onClick={vm.removeAvatar}
                >
                  {t('Удалить фото')}
                </button>
              )}
            </div>
            {vm.avatarError && <span className="BProfile__name-error">{vm.avatarError}</span>}
          </div>
        </section>

        {/* Student stats — level is client-derived from XP (500 XP/level) */}
        {vm.stats && vm.level && (
          <section className="b-card BProfile__card BProfile__stats">
            <div className="BProfile__level-row">
              <span className="BProfile__level">
                {/* Bileli: __level/__stat spans are plain inline (no flex gap) — the {' '} does the spacing */}
                <Icon name="medal" />
                {' '}
                {t('Уровень')}
                {' '}
                {vm.level.level}
              </span>
              <span className="b-text-muted BProfile__level-next">
                {vm.level.toNext}
                {' '}
                {t('XP до следующего')}
              </span>
            </div>
            <div className="b-progress b-progress-thin">
              <div className="b-progress-bar" style={`width:${vm.level.pct}%`} />
            </div>
            <div className="BProfile__stat-pills">
              <span className="BProfile__stat">
                <Icon name="zap" />
                {' '}
                {vm.stats.totalXp}
                {' '}
                XP
              </span>
              <span className="BProfile__stat">
                <Icon name="flame" />
                {' '}
                {vm.stats.currentStreak}
                {' '}
                {t('дн. подряд')}
              </span>
              <span className="BProfile__stat">
                <Icon name="trophy" />
                {' '}
                {t('Рекорд')}
                {' '}
                {vm.stats.longestStreak}
                {' '}
                {t('дн.')}
              </span>
            </div>
          </section>
        )}

        {/* Interface settings */}
        <section className="b-card BProfile__card BProfile__settings">
          <div className="BProfile__setting">
            <span className="BProfile__setting-label">{t('Язык интерфейса')}</span>
            <div className="BProfile__seg">
              <button
                type="button"
                className={`BProfile__seg-btn ${vm.lang === 'ru' ? 'active' : ''}`}
                onClick={() => vm.setLang('ru')}
              >
                Русский
              </button>
              <button
                type="button"
                className={`BProfile__seg-btn ${vm.lang === 'ky' ? 'active' : ''}`}
                onClick={() => vm.setLang('ky')}
              >
                Кыргызча
              </button>
            </div>
          </div>
          <div className="BProfile__setting">
            <span className="BProfile__setting-label">{t('Тема')}</span>
            <div className="BProfile__seg">
              <button
                type="button"
                className={`BProfile__seg-btn ${vm.theme === 'dark' ? 'active' : ''}`}
                onClick={() => vm.setTheme('dark')}
              >
                {/* Bileli: __seg-btn is a plain inline button — explicit {' '} spaces icon/label */}
                <Icon name="moon" />
                {' '}
                {t('Тёмная')}
              </button>
              <button
                type="button"
                className={`BProfile__seg-btn ${vm.theme === 'light' ? 'active' : ''}`}
                onClick={() => vm.setTheme('light')}
              >
                <Icon name="sun" />
                {' '}
                {t('Светлая')}
              </button>
            </div>
          </div>
        </section>

        {/* Bileli account — Finik order history (any user can buy courses, so shown in both modes). */}
        <section className="b-card BProfile__card BProfile__tg">
          <button type="button" className="BProfile__tg-row" onClick={() => navigate('my-orders')}>
            <span>
              <Icon name="receipt" />
              {' '}
              {t('Мои заказы')}
            </span>
            <span aria-hidden><Icon name="chevron-right" /></span>
          </button>
        </section>

        {/* Telegram hand-offs — avatar/username/language of the Telegram half live in Telegram settings */}
        <section className="b-card BProfile__card BProfile__tg">
          <button type="button" className="BProfile__tg-row" onClick={vm.openTelegramProfile}>
            <span>
              <Icon name="send" />
              {' '}
              {t('Профиль и фото в Telegram')}
            </span>
            <span aria-hidden><Icon name="chevron-right" /></span>
          </button>
          <button type="button" className="BProfile__tg-row" onClick={vm.openTelegramLanguage}>
            <span>
              <Icon name="globe" />
              {' '}
              {t('Язык Telegram (кыргызча пакети — в списке)')}
            </span>
            <span aria-hidden><Icon name="chevron-right" /></span>
          </button>
        </section>
      </div>
    </div>
  );
};

type NameFieldProps = {
  initial: string;
  isSaving: boolean;
  error?: string;
  onSave: (v: string) => void;
};

// Inline blur-save name editor (state keyed by user.id upstream so a session refresh re-seeds it).
const NameField: FC<NameFieldProps> = ({ initial, isSaving, error, onSave }) => {
  const [value, setValue] = useState(initial);

  return (
    <div className="BProfile__name">
      <input
        className="b-input BProfile__name-input"
        value={value}
        placeholder={t('Ваше имя')}
        disabled={isSaving}
        onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)}
        onBlur={() => {
          if (value.trim() && value.trim() !== initial) onSave(value);
        }}
      />
      {isSaving && <span className="b-text-muted BProfile__name-status">{t('сохраняю…')}</span>}
      {error && <span className="BProfile__name-error">{error}</span>}
    </div>
  );
};

export default memo(withGlobal(
  (global): StateProps => ({
    user: global.bileliAuth?.user,
  }),
)(ProfilePanel));
